const TOKEN_KEY = 'nb_token';
const USER_KEY = 'nb_user';
const CLIENT_KEY = 'nb_client';
const DEVICE_KEY = 'nb_device';

export type Role = 'USER' | 'CREATOR' | 'ADMIN';

export interface SessionUser {
  id: string;
  email: string;
  firstname?: string;
  lastname?: string;
  roles: Role[];
  mustChangePassword?: boolean;
}

interface LoginResponse {
  id: string;
  email: string;
  token: string;
  roles: Role[];
  firstname?: string;
  lastname?: string;
  mustChangePassword?: boolean;
}

class Session {
  token: string | null;
  user: SessionUser | null;
  clientId: string;
  /** Stable per-browser identifier — sent as `Device-Id` for backend restriction checks. */
  deviceId: string;

  constructor() {
    this.token = localStorage.getItem(TOKEN_KEY);
    this.user = (() => {
      const raw = localStorage.getItem(USER_KEY);
      try { return raw ? (JSON.parse(raw) as SessionUser) : null; } catch { return null; }
    })();
    // clientId is the user id returned at login (the backend keys its auth-token cache on
    // Pair(clientId, token) where clientId is set from the login response). Pre-login this
    // is just a placeholder so unauthenticated calls still have a header value.
    this.clientId = this.user?.id || localStorage.getItem(CLIENT_KEY) || (() => {
      const id = uuid();
      localStorage.setItem(CLIENT_KEY, id);
      return id;
    })();
    this.deviceId = localStorage.getItem(DEVICE_KEY) || (() => {
      const id = uuid();
      localStorage.setItem(DEVICE_KEY, id);
      return id;
    })();
  }

  save(response: LoginResponse, fallbackEmail?: string) {
    this.token = response.token;
    this.user = {
      id: response.id,
      email: response.email || fallbackEmail || '',
      firstname: response.firstname,
      lastname: response.lastname,
      roles: response.roles || [],
      mustChangePassword: response.mustChangePassword,
    };
    this.clientId = response.id;
    localStorage.setItem(TOKEN_KEY, this.token);
    localStorage.setItem(USER_KEY, JSON.stringify(this.user));
    localStorage.setItem(CLIENT_KEY, response.id);
  }

  clear() {
    this.token = null;
    this.user = null;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    // Keep CLIENT_KEY around — it's harmless for the unauthenticated /auth handshake
    // and avoids a fresh UUID each sign-out.
  }

  hasRole(r: Role) { return !!this.user?.roles?.includes(r); }
  isAdmin() { return this.hasRole('ADMIN'); }
}

export const session = new Session();

export function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
